import React from 'react'
import classnames from 'classnames'
import HeartRating from 'components/heart-rating'
import Link from 'components/link'
import css from './ProductCardV2.styles.scss'
import ProductImage, { Overlay } from '../ProductImage/ProductImage'
import PriceTag, { PriceTagProps } from '../PriceTag/PriceTag'
import PromoBadge, { PromoBadgeProps } from '../PromoBadge/PromoBadge'
import {
  ELECTRONIC_GIFT_CARD_MIN,
  ELECTRONIC_GIFT_CARD_MAX,
} from 'shared/constants'
import { ProductCallouts } from 'components/ProductCallouts/ProductCallouts'
import { track, useTracking } from 'lib/analytics/track'
import {
  PRODUCT_SEARCH_INDEX_KEY,
  getSearchId,
} from 'shared/hooks/useSearchId/useSearchId'
import { getSearchPreviewIds } from 'components/global-nav/components/SearchBar/SearchBar.utils'
import { ProductCallout } from 'src/types/productCallout'

interface Price {
  msrp?: number
  min?: number
  max?: number
  current?: number
}

interface ProductCardV2Props {
  productId: number
  brand?: string
  title: string
  imageUrl: string
  secondaryImageUrl?: string
  path: string
  slugSet?: string
  lazyLoad?: boolean
  fsaHsaEligible?: boolean
  isCashFund: boolean
  isCrowdFund: boolean
  isElectronicGiftCard: boolean
  isFavor: boolean
  isRegistryOwner?: boolean
  reviewCount?: number
  rating?: number
  optionsCta?: string
  position?: number
  price?: Price
  merchandisingMessage?: string
  promotionalBadges?: PromoBadgeProps[]
  callouts?: ProductCallout[]
  currentlyInActiveSale?: boolean
  __forcePageLoad?: boolean
}

const ProductCardV2: React.FC<ProductCardV2Props> = ({
  productId,
  brand,
  title,
  imageUrl,
  secondaryImageUrl,
  path,
  slugSet,
  fsaHsaEligible,
  // lazyLoad, // TODO - lazy load images
  isCashFund,
  isCrowdFund,
  isElectronicGiftCard,
  isFavor,
  isRegistryOwner,
  reviewCount,
  rating,
  optionsCta,
  position,
  price,
  merchandisingMessage,
  promotionalBadges,
  callouts,
  currentlyInActiveSale,
  __forcePageLoad = false,
}) => {
  const tracker = useTracking()

  const productPrice = Number.isNaN(price?.min) ? undefined : price?.min
  const productType = isElectronicGiftCard
    ? track.ProductType.GIFT_CARD
    : track.ProductType.PRODUCT
  const searchId = getSearchId(PRODUCT_SEARCH_INDEX_KEY)
  const searchPreviewIds = getSearchPreviewIds()

  const trackProductClick = (eventType: string, eventLocation: string) => {
    tracker.trackEvent({
      event: track.productClicked,
      eventType,
      eventLocation,
      isRegistryOwner,
      isFsaHsaEligible: fsaHsaEligible,
      positionInList: position,
      productIds: [productId],
      productPrice,
      productType,
      searchId,
      searchPreviewIds,
    })
  }

  const priceTagProps = getPriceTagProps(
    price,
    isElectronicGiftCard,
    currentlyInActiveSale
  )

  const supportsRatings = () => {
    return (
      !isCashFund &&
      !isFavor &&
      !isElectronicGiftCard &&
      !isCrowdFund &&
      !!reviewCount
    )
  }

  const imageAlt = `${brand || ''} ${title}`
  const images = [
    {
      alt: imageAlt,
      src: imageUrl,
    },
  ]
  if (secondaryImageUrl) {
    images.push({
      alt: imageAlt,
      src: secondaryImageUrl,
    })
  }

  const overlays = getProductImageOverlays(promotionalBadges)

  const linkAction = __forcePageLoad
    ? { url: path }
    : { to: { pathname: path, state: { slugSet } } }

  return (
    <>
      <div className={classnames(css['ProductCardV2'])}>
        <Link
          {...linkAction}
          data-testid="product-link"
          onClick={() =>
            trackProductClick(
              track.EventType.CLICK,
              track.EventLocation.STORE_MINUS_PRODUCT_CARD
            )
          }
        >
          <ProductImage images={images} overlays={overlays} />
          <div className={classnames(css['ProductCardV2__contentWrapper'])}>
            <h3 className="flex col unset-all">
              {brand && (
                <div
                  data-testid="brand"
                  className={classnames(css['ProductCardV2__brand'])}
                >
                  {brand}
                </div>
              )}
              <div
                data-testid="title"
                className={classnames(css['ProductCardV2__title'])}
              >
                {title}
              </div>
            </h3>
            {optionsCta && (
              <div
                data-testid="optionsCta"
                className={classnames(css['ProductCardV2__optionsCta'])}
              >
                {optionsCta}
              </div>
            )}
            {supportsRatings() && (
              <HeartRating
                rating={rating || 0}
                reviewCount={reviewCount || 0}
              />
            )}
            {priceTagProps && <PriceTag {...priceTagProps} />}
            {merchandisingMessage && (
              <div
                className={classnames(
                  css['ProductCardV2__merchandisingMessage']
                )}
              >
                {merchandisingMessage}
              </div>
            )}
            {callouts && (
              <div className={classnames(css['ProductCardV2__callouts'])}>
                <ProductCallouts callouts={callouts} textSize={13} />
              </div>
            )}
          </div>
        </Link>
      </div>
    </>
  )
}

const getPriceTagProps = (
  price?: Price,
  isElectronicGiftCard?: boolean,
  currentlyInActiveSale?: boolean
): PriceTagProps | null => {
  if (!price) return null

  if (isElectronicGiftCard) {
    return {
      minPrice: ELECTRONIC_GIFT_CARD_MIN,
      maxPrice: ELECTRONIC_GIFT_CARD_MAX,
      truncateZeroCents: true,
    }
  }

  return {
    msrp: price?.msrp,
    minPrice: price?.min,
    maxPrice: price?.max,
    currentPrice: price?.current,
    currentlyInActiveSale,
  }
}

const getProductImageOverlays = (
  promotionalBadges?: PromoBadgeProps[]
): Overlay[] => {
  return (
    promotionalBadges?.map((badge, index) => {
      const badgePosition = badge.type === 'text' ? 'top-left' : 'top-right'
      return {
        key: `badge-${badgePosition}-${index}`,
        position: badgePosition,
        hideOnHover: true,
        content: <PromoBadge {...badge} />,
      }
    }) || []
  )
}

export default ProductCardV2
